import {
	canTakeBackApproval,
	getApprovalChain,
	getApprovalTurn,
	getSkippedByDefault,
} from "@msuite/utils/incoming-invoices";
import type { IncomingInvoiceApprovalListItem, IncomingInvoiceDetail } from "@msuite/utils/types";
import type { ApprovalRow } from "./approval-row";

/**
 * The invoice's approvals as table rows, in the order the chain decides them: each handed-over
 * step numbered from the newest, the default approvers sharing the number after the last, and
 * every approval outside the chain — a default one beside a sole approval — last and unnumbered.
 *
 * @param incomingInvoice - The invoice, with its approvals.
 * @returns One row per approval, in decision order.
 */
export function toApprovalRows(incomingInvoice: IncomingInvoiceDetail | undefined): ApprovalRow[] {
	if (!incomingInvoice) return [];
	const approvals = incomingInvoice.approvals.map((approval) => ({
		...approval,
		userId: approval.user.id,
	}));
	const chain = getApprovalChain(approvals);
	const skipCount = getSkippedByDefault(approvals).length;
	const chainOrder = getChainOrder(chain);
	const outside = approvals.filter((approval) => !chainOrder.has(approval.id));
	return [...chain, ...outside].map((approval) => ({
		...approval,
		incomingInvoiceId: incomingInvoice.id,
		isInvoicePaid: incomingInvoice.state === "paid",
		isInvoiceDuplicate: incomingInvoice.state === "duplicate",
		chainOrder: chainOrder.get(approval.id) ?? null,
		turn: getApprovalTurn(approval, approvals),
		canTakeBack: canTakeBackApproval(approval, approvals),
		skipCount,
	}));
}

/**
 * The number each step of the chain decides at. Sole approvals each decide whenever they like and
 * so all carry 1; the default approvers are one pool and share the number after the last step.
 */
function getChainOrder(
	chain: Array<Pick<IncomingInvoiceApprovalListItem, "id" | "assignment" | "isSoleApprover">>,
) {
	const order = new Map<string, number>();
	let position = 0;
	let defaultPosition: number | null = null;
	for (const approval of chain) {
		if (approval.isSoleApprover) {
			order.set(approval.id, 1);
			continue;
		}
		if (approval.assignment === "default") {
			defaultPosition ??= position + 1;
			order.set(approval.id, defaultPosition);
			continue;
		}
		position += 1;
		order.set(approval.id, position);
	}
	return order;
}
